<template>
  <div class="event-card flex-1 large flex flex-row" :class="type">
    <div class="card-image relative">
      <w-navigation
        :object="event"
        :params="{
          name: 'EventProfile',
          params: {
            slug: event.slug
          }
        }"
        :blank="event.has_waiting_room"
        :is-link="isLink"
        :is-event="true"
        class="h-full"
      >
        <template #link>
          <img
            :src="getImageUrl(event)"
            :alt="event.title"
            class="main-image"
            loading="lazy"
            @error="
              $event.target.src = require('@/assets/images/placeholder-event.svg')
            "
          />
        </template>
      </w-navigation>
    </div>

    <div class="card-info flex-1">
      <div
        class="info flex flex-col md:justify-normal justify-between md:gap-6"
      >
        <div class="header flex flex-col">
          <w-navigation
            :object="event"
            :params="{
              name: 'EventProfile',
              params: {
                slug: event.slug
              }
            }"
            :blank="event.has_waiting_room"
            :is-link="isLink"
          >
            <template #link>
              <span
                class="font-museo-500"
                :class="type !== 'profiles' ? 'text-m' : 'title-card'"
                v-html="nameFormatted"
              ></span>
            </template>
          </w-navigation>

          <w-navigation
            v-if="event.venue"
            :object="event.venue"
            :params="{
              name: 'VenueProfile',
              params: {
                slug: event.venue.venue
              }
            }"
            :blank="openExternal"
            :is-link="isLink"
          >
            <template #link>
              <span
                class="font-museo-500 text-color-topaz-light"
                :class="type !== 'profiles' ? 'text-s' : 'subtitle-card'"
                >{{ event.venue?.name }}
                {{ event.city?.name ? "- " + event.city.name : "" }}</span
              >
            </template>
          </w-navigation>
        </div>
        <div
          v-if="event.price || event.price === 0"
          class="large-card__price font-museo-500 inline-block md:hidden"
        >
          {{ formatCurrency(Number(event.price).toFixed(2), event.currency) }}
          <!-- TODO: revisar si es necesario el fixed -->
        </div>
        <div
          class="date-info text-color-gray font-museo-500 hidden md:flex flex-row gap-x-2 md:mt-0 mt-[14px] flex-wrap"
          :class="type !== 'profiles' ? 'text-s' : 'subtitle-card'"
        >
          <div class="day">
            <img
              :src="require('@/assets/images/ic-calendar.svg')"
              :alt="$t('wegow.altIcon')"
              class="close-icon cursor-pointer lazyload w-[0.875rem] h-[0.875rem]"
            />
            <span>{{ eventDate }}</span>
          </div>
          <div class="hour">
            <img
              :src="require('@/assets/images/ic-clock.svg')"
              :alt="$t('wegow.altIcon')"
              class="close-icon cursor-pointer lazyload w-[0.875rem] h-[0.875rem]"
            />
            <span>{{ eventHour }}</span>
          </div>
        </div>
      </div>
      <div class="payment flex flex-row md:flex-col justify-between text-right">
        <div
          v-if="event.price || event.price === 0"
          class="large-card__price font-museo-500 md:inline-block hidden"
        >
          {{ formatCurrency(Number(event.price).toFixed(2), event.currency) }}
          <!-- TODO: revisar si es necesario el fixed -->
        </div>
        <div
          class="date-info text-color-gray font-museo-500 md:hidden flex flex-row gap-x-2 md:mt-0 mt-[14px] flex-wrap"
          :class="type !== 'profiles' ? 'text-s' : 'subtitle-card'"
        >
          <div class="day">
            <img
              :src="require('@/assets/images/ic-calendar.svg')"
              :alt="$t('wegow.altIcon')"
              class="close-icon cursor-pointer lazyload w-[0.875rem] h-[0.875rem]"
            />
            <span>{{ eventDate }}</span>
          </div>
          <div class="hour">
            <img
              :src="require('@/assets/images/ic-clock.svg')"
              :alt="$t('wegow.altIcon')"
              class="close-icon cursor-pointer lazyload w-[0.875rem] h-[0.875rem]"
            />
            <span>{{ eventHour }}</span>
          </div>
        </div>
        <div v-if="!event.queued">
          <div
            v-if="
              event.enabled &&
                (event.ticket_types_count > 0 || event.has_options)
            "
            class="purchase"
          >
            <w-navigation
              v-if="!createPurchase"
              :object="{}"
              :params="{
                name: 'PurchasePage',
                params: {
                  slug: event.slug
                }
              }"
              :blank="openExternal"
            >
              <template #link>
                <wegow-button
                  :button-id="`purchase-button-${event.id}`"
                  background="opacity"
                  color="topaz-light"
                  type="button"
                  :text="$t('wegow.purchase.buy')"
                />
              </template>
            </w-navigation>
            <wegow-button
              v-else
              :button-id="`purchase-button-${event.id}`"
              background="opacity"
              color="topaz-light"
              type="button"
              :text="$t('wegow.purchase.buy')"
              @click="$emit('clickPurchase')"
            />
          </div>
        </div>
        <div v-else>
          <wegow-button
            :button-id="`purchase-queued-button-${event.id}`"
            background="opacity"
            color="topaz-light"
            type="button"
            :text="$t('wegow.purchase.buy')"
            @click="onClickQueued(event)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, toRefs, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";

import WegowButton from "@/components/general/forms/WegowButton";
import WNavigation from "@/components/general/ui/WNavigation";

import { useEvent } from "@/composables/useEvent";
import { useUser } from "@/composables/useUser";

import { formatCurrency } from "@/plugins/currency";
import { formatHour, formatDate } from "@/plugins/date-formater";
import { useModalHandler } from "@/plugins/useModalHandler";

const props = defineProps({
  isLink: {
    type: Boolean,
    default: true
  },
  event: {
    type: Object,
    required: true
  },
  type: {
    type: String,
    default: undefined
  },
  openExternal: {
    type: Boolean,
    default: false
  },
  createPurchase: {
    type: Boolean,
    default: false
  },
  imgHeight: {
    type: String,
    default: "51%"
  }
});

const emit = defineEmits("emptyAd", "clickPurchase");

const { event, type } = toRefs(props);
const route = useRoute();
const { selectedRegion } = useUser();
const { openErrorModal } = useModalHandler();
const { t } = useI18n();
const timezone = ref("Europe/Madrid");
const showAd = ref(true);
const isCompany = ref(false);

const { hasCulturalBonus, addToWaitingList } = useEvent();

onMounted(async () => {
  timezone.value = (await Intl.DateTimeFormat())
    .resolvedOptions()
    .timeZone.trim();
  // if (props.event.add) {
  //   showAd.value = false;
  // }
  if (route.name === "CompanyPage") {
    isCompany.value = true;
  }
});
// Computer
const eventDate = computed(() =>
  formatDate(event.value.start_date, selectedRegion())
);
const eventHour = computed(() =>
  formatHour(event.value.start_date, route.params.region)
);
const nameFormatted = computed(() => {
  let title = event.value.title;
  const replaceStrings = [
    t("wegow.headerDetails.start"),
    t("wegow.headerDetails.startStreaming")
  ];
  const replace = new RegExp(`(${replaceStrings.join(")|(")})`);
  return title?.replace(replace, "");
});

// Methods
const getImageUrl = element =>
  element?.image_url ||
  element?.thumbnails?.s_cd ||
  require("@/assets/images/placeholder-event.svg");

const emptyAd = $event => {
  emit("emptyAd", $event);
};
const goOutWaitingList = () => {
  openErrorModal({
    modal: {
      imageType: "none",
      showClose: false,
      title: t("wegow.purchase.goOutWaitingList"),
      btn_message: t("general.accept"),
      btn_red_message: t("general.cancel"),
      button_method: () => {
        addToWaitingList(event.value.id);
        event.value.user.waiting_weswap = false;
      }
    }
  });
};
const onClickQueued = event => {
  window.open(event.queue_link, "_blank");
};
</script>

<style lang="scss" scoped>
.large-card__price {
  font-size: 11px;
  @media screen and (min-width: $screen-md) {
    font-size: 20px;
  }
}
.people-icon {
  width: 23px;
  height: 23px;
}
.large {
  background-color: #1c1d29;
  border-radius: 6px;
  overflow: hidden;
  min-width: 100%;
  &.small {
    color: $textSubtitle;
    @media screen and (min-width: $screen-md) {
      min-width: unset;
      max-width: 565px;
      height: 120px;
      flex-direction: row;
    }
    .event-date {
      font-size: 10px;
    }
    .event-name {
      font-size: 11px;
      @media screen and (min-width: $screen-md) {
        font-size: 1rem;
      }
    }
    .people-icon {
      width: 11px;
      height: 11px;
      font-size: 8px;
    }
    .cultural-icon {
      width: 24px;
      height: 23px;
    }
    .card-info {
      width: 70%;
      display: flex;
      flex-direction: column;
    }
    .card-image {
      min-height: 100%;
      width: 116px;
      height: 120px;
    }
  }
  .event-card-navigation {
    display: flex;
    flex-direction: row;
  }
  //   .card-image {
  //     width: 100%;
  //     height: 160px;
  //     @media screen and (min-width: $screen-md) {
  //       height: auto;
  //       width: 170px;
  //     }
  //     @media screen and (min-width: $screen-lg) {
  //       height: 160px;
  //       min-height: 160px;
  //       width: 160px;
  //     }
  //     img:not(.cultural-icon) {
  //       height: 100%;
  //       width: 100%;
  //       object-fit: cover;
  //     }
  //   }
  .card-info {
    @apply m-6 flex md:flex-row flex-col justify-between;
    .day,
    .hour {
      @apply flex flex-row gap-2 items-center;
      font-size: 9px;
      @media screen and (min-width: $screen-md) {
        font-size: 12px;
      }
    }
    .purchase {
      margin-top: auto;
    }
  }
}
.title-card {
  font-size: 12px;
  @media screen and (min-width: $screen-md) {
    font-size: 15px;
  }
}
.subtitle-card {
  font-size: 11px;
  @media screen and (min-width: $screen-md) {
    font-size: 12px;
  }
}
// Profiles
.profiles {
  @media screen and (min-width: $screen-md) {
    height: 100%;
  }
  .card-image {
    // height: 108px;
    width: 108px;
    @media screen and (min-width: $screen-md) {
      // min-height: 120px;
      //   height: 120px;
      width: 120px;
    }

    img {
      object-fit: cover;
      width: 100%;
      height: 100%;
    }
  }
  .card-info {
    margin: 0;
    padding: 13.5px 11px;
    @media screen and (min-width: $screen-md) {
      padding: 23px 19px;
    }
  }
}
</style>
<style lang="scss">
.large {
  small {
    color: $textSubtitle;
  }
}
</style>
